/**
 * Users service — Platform API wrappers for the caller's own profile.
 *
 * Reads and writes go to `/platform/v1/users/me` (self-scoped by the caller's
 * WorkOS identity; no id is sent by the client).
 */

import { idempotencyHeaders, platformErrorMessage, platformGet, platformPatch } from '@/apps/ingestion/services/_platformClient';

const ME_PATH = '/platform/v1/users/me';

export interface PlatformUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url: string | null;
  avatar_color: string | null;
  finance_tour_completed: boolean | null;
}

export type UpdateMePatch = Partial<
  Pick<PlatformUser, 'first_name' | 'last_name' | 'avatar_url' | 'avatar_color' | 'finance_tour_completed'>
>;

/** Fetch the current user's profile. Throws on transport/permission errors. */
export async function getMe(): Promise<PlatformUser> {
  const { data, error } = await platformGet(ME_PATH);
  if (error || !data) {
    throw new Error(platformErrorMessage(error, 'Failed to load current user'));
  }
  return data as PlatformUser;
}

/** Partially update the current user's profile. Returns the updated row. */
export async function updateMe(patch: UpdateMePatch): Promise<PlatformUser> {
  const { data, error } = await platformPatch(ME_PATH, {
    body: patch,
    headers: idempotencyHeaders(),
  });
  if (error || !data) {
    throw new Error(platformErrorMessage(error, 'Failed to update profile'));
  }
  return data as PlatformUser;
}
